import styled from '@emotion/styled';
import { css } from '@emotion/react';

import {
  spacing,
  borderRadius,
  typography,
  transitions,
  colors,
} from '../tokens';

export type InputSize = 'sm' | 'md' | 'lg' | 'xl';
export type InputShape = 'default' | 'pill';

/**
 * Valores estimados a partir do print do Figma (node 9487:1440) — input "pill"
 * com placeholder "Buscar por" e lupa à direita.
 */
const spec = {
  border: '#595959',
  borderFocus: '#3D3D3D',
  borderDisabled: '#D9D9D9',
  text: '#3D3D3D',
  placeholder: '#8C8C8C',
  icon: '#3D3D3D',
};

type WrapperProps = {
  $disabled: boolean;
  $fullWidth: boolean;
  $size: InputSize;
  $shape: InputShape;
  $error?: boolean;
};

/** Alinhado com Select, DateInput e Dropdown (sm/md/lg). `xl` = busca grande (Figma). */
const wrapperSizeStyles: Record<InputSize, ReturnType<typeof css>> = {
  sm: css`
    height: 32px;
    padding: 0 ${spacing.md};
    gap: ${spacing.sm};
  `,
  md: css`
    height: 38px;
    padding: 0 ${spacing.lg};
    gap: ${spacing.sm};
  `,
  lg: css`
    height: 44px;
    padding: 0 ${spacing.xl};
    gap: ${spacing.md};
  `,
  xl: css`
    height: 56px;
    padding: 0 ${spacing['2xl']};
    gap: ${spacing.md};
  `,
};

const inputSizeStyles: Record<InputSize, ReturnType<typeof css>> = {
  sm: css`
    font-size: ${typography.fontSize.sm};
  `,
  md: css`
    font-size: ${typography.fontSize.md};
  `,
  lg: css`
    font-size: ${typography.fontSize.lg};
  `,
  xl: css`
    font-size: ${typography.fontSize.lg};
  `,
};

const iconSizeStyles: Record<InputSize, ReturnType<typeof css>> = {
  sm: css`
    svg {
      width: 16px;
      height: 16px;
    }
  `,
  md: css`
    svg {
      width: 16px;
      height: 16px;
    }
  `,
  lg: css`
    svg {
      width: 16px;
      height: 16px;
    }
  `,
  xl: css`
    svg {
      width: 24px;
      height: 24px;
    }
  `,
};

const shapeStyles: Record<InputShape, ReturnType<typeof css>> = {
  default: css`
    border-radius: ${borderRadius.md};
  `,
  pill: css`
    border-radius: ${borderRadius.full};
  `,
};

export const FieldRoot = styled('div', {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<{ $fullWidth: boolean }>`
  display: ${({ $fullWidth }) => ($fullWidth ? 'flex' : 'inline-flex')};
  flex-direction: column;
  gap: 6px;
  width: ${({ $fullWidth }) => ($fullWidth ? '100%' : 'auto')};
`;

/** Alinhado com Select e DateInput para campos ficarem na mesma linha. */
export const FieldLabel = styled('label')`
  font-family: ${typography.fontFamily.primary};
  font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.bold};
  line-height: ${typography.lineHeight.tight};
  color: #3d3d3d;
`;

export const RequiredMark = styled('span')`
  color: ${colors.error};
`;

export const InputWrapper = styled('label', {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<WrapperProps>`
  display: inline-flex;
  align-items: center;
  box-sizing: border-box;
  width: ${({ $fullWidth }) => ($fullWidth ? '100%' : 'auto')};
  background-color: #ffffff;
  border: 1px solid
    ${({ $disabled, $error }) => {
      if ($error) return colors.error;
      return $disabled ? spec.borderDisabled : spec.border;
    }};
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'text')};
  opacity: ${({ $disabled }) => ($disabled ? 0.6 : 1)};
  transition: border-color ${transitions.base};

  ${({ $size }) => wrapperSizeStyles[$size]}
  ${({ $shape }) => shapeStyles[$shape]}

  &:focus-within {
    border-color: ${({ $error }) => ($error ? colors.error : spec.borderFocus)};
  }
`;

export const StyledInput = styled('input')<{ $size: InputSize }>`
  flex: 1;
  min-width: 0;
  border: none;
  outline: none;
  background: transparent;
  font-family: ${typography.fontFamily.primary};
  font-weight: ${typography.fontWeight.regular};
  color: ${spec.text};

  ${({ $size }) => inputSizeStyles[$size]}

  &::placeholder {
    color: ${spec.placeholder};
    opacity: 1;
  }

  &:disabled {
    cursor: not-allowed;
  }
`;

export const IconSlot = styled('span')<{ $size: InputSize }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: ${spec.icon};
  line-height: 0;

  ${({ $size }) => iconSizeStyles[$size]}
`;
